"use client";

/**
 * The brand mark that comes alive: the static tile until the first
 * pointer, focus or touch, then <MenzilLogo /> (the brush strokes whirl
 * into a house and back) loaded on demand, so pages that never hover the
 * logo never download its 58 KB of geometry. `active` forces the house
 * (the waiting states use it); otherwise hovering this element, or the
 * children it wraps (the wordmark next to it), plays it.
 */
import { Suspense, lazy, useCallback, useEffect, useRef, useState, type ReactNode } from "react";
import { useT } from "@/lib/locale-context";
import { trackEvent } from "@/lib/tracking";

const MenzilLogo = lazy(() => import("./MenzilLogo"));

const TOUCH_HOLD_MS = 2600;
/** The geometry loads this long after mount, while nobody is looking, so
 *  the swap from the static tile never happens under the pointer. */
const PRELOAD_AFTER_MS = 1800;

export default function AnimatedLogoMark({
  size = 28,
  active,
  loop = false,
  className = "",
  markClassName = "",
  children,
}: {
  /** The mark's side in px. */
  size?: number;
  /** Force the house: the waiting states. */
  active?: boolean;
  /** With `active`: keep going round instead of holding the house, for a wait
   *  that may take a while (the analysis, a reply being written). */
  loop?: boolean;
  className?: string;
  /** Classes for the square the mark sits in (responsive sizes). */
  markClassName?: string;
  /** Rendered after the mark inside the hover area, e.g. the wordmark. */
  children?: ReactNode;
}) {
  const t = useT();
  const [wanted, setWanted] = useState(Boolean(active));
  const [hover, setHover] = useState(false);
  const holdTimer = useRef<number | undefined>(undefined);

  useEffect(() => {
    if (active) setWanted(true);
  }, [active]);
  useEffect(() => () => window.clearTimeout(holdTimer.current), []);
  useEffect(() => {
    const id = window.setTimeout(() => setWanted(true), PRELOAD_AFTER_MS);
    return () => window.clearTimeout(id);
  }, []);

  // Once per mount: who actually saw the house (hover, focus or tap).
  const seen = useRef(false);
  const noteSeen = useCallback((via: "hover" | "focus" | "tap") => {
    if (seen.current) return;
    seen.current = true;
    trackEvent("brand.logo.seen", { via, forced: Boolean(active) });
  }, [active]);

  const on = useCallback(() => {
    window.clearTimeout(holdTimer.current);
    setWanted(true);
    setHover(true);
  }, []);
  const off = useCallback(() => setHover(false), []);
  const tap = useCallback(() => {
    on();
    noteSeen("tap");
    holdTimer.current = window.setTimeout(() => setHover(false), TOUCH_HOLD_MS);
  }, [noteSeen, on]);

  const still = (
    // eslint-disable-next-line @next/next/no-img-element
    <img src="/logo.svg" alt={t("brand")} width={size} height={size} className="block h-full w-full" />
  );

  return (
    <span
      className={`inline-flex items-center gap-2 ${className}`}
      onPointerEnter={(e) => { if (e.pointerType !== "touch") { on(); noteSeen("hover"); } }}
      onPointerLeave={(e) => { if (e.pointerType !== "touch") off(); }}
      onTouchStart={tap}
      onFocus={() => { on(); noteSeen("focus"); }}
      onBlur={off}
      onClick={() => trackEvent("brand.logo.click", { house: hover || Boolean(active) })}
    >
      <span className={`block shrink-0 ${markClassName}`} style={markClassName ? undefined : { width: size, height: size }}>
        {wanted ? (
          <Suspense fallback={still}>
            <MenzilLogo size="100%" lift={false} active={active ?? hover} loop={loop && Boolean(active)} aria-label={t("brand")} />
          </Suspense>
        ) : still}
      </span>
      {children}
    </span>
  );
}
